import React, { useState, useEffect, useRef, useCallback } from 'react';
import './History.css';
import { API_BASE_URL } from '../config';
import ManualSleepEntry from '../components/ManualSleepEntry';
import { useToast } from '../contexts/ToastContext';

interface Baby {
  id: string;
  name: string;
  user_id: string;
  created_at: string;
  is_owner?: boolean;
  owner_username?: string;
}

interface SleepEntry {
  id: string;
  baby_id: string;
  start_time: string;
  end_time: string | null;
  created_at: string;
  is_ongoing: boolean;
  duration_minutes: number | null;
}

interface HistoryProps {
  user: any;
}

const History: React.FC<HistoryProps> = ({ user }) => {
  const [babies, setBabies] = useState<Baby[]>([]);
  const [sleepEntries, setSleepEntries] = useState<SleepEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [selectedSleepEntry, setSelectedSleepEntry] = useState<SleepEntry | null>(null);
  const [isEditingEntry, setIsEditingEntry] = useState(false);
  const sleepListRef = useRef<HTMLDivElement>(null);

  const { showToast } = useToast();

  // Find active baby based on user's active_baby_id
  const activeBaby = babies.find(baby => baby.id === user.active_baby_id) || babies[0];

  // Group sleep entries by date
  const groupEntriesByDate = (entries: SleepEntry[]) => {
    const groups: { [key: string]: SleepEntry[] } = {};

    entries.forEach(entry => {
      const entryDate = new Date(entry.start_time);
      const dateKey = entryDate.toDateString();

      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(entry);
    });

    return groups;
  };

  // Get date header text
  const getDateHeader = (dateString: string) => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const entryDate = new Date(dateString);

    if (entryDate.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (entryDate.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return entryDate.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
  };

  const fetchBabies = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/babies/user/${user.id}`);
      if (response.ok) {
        const data = await response.json();
        setBabies(data);
      }
    } catch (error) {
      console.error('Failed to fetch babies:', error);
    }
  }, [user.id]);

  const fetchSleepEntries = useCallback(async () => {
    if (!activeBaby) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/sleep/baby/${activeBaby.id}`);
      if (response.ok) {
        const data = await response.json();
        setSleepEntries(data);
      }
    } catch (error) {
      console.error('Failed to fetch sleep entries:', error);
    } finally {
      setLoading(false);
    }
  }, [activeBaby]);

  useEffect(() => {
    if (user) {
      fetchBabies();
    }
  }, [user, fetchBabies]);

  useEffect(() => {
    if (activeBaby) {
      fetchSleepEntries();
    }
  }, [activeBaby, fetchSleepEntries]);

  // Auto-scroll to bottom when sleep entries change
  useEffect(() => {
    if (sleepListRef.current && sleepEntries.length > 0) {
      sleepListRef.current.scrollTop = sleepListRef.current.scrollHeight;
    }
  }, [sleepEntries]);

  const handleManualSleepSubmit = async (startTime: Date, endTime: Date) => {
    if (!activeBaby) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/sleep/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          baby_id: activeBaby.id,
          start_time: startTime.toISOString(),
          end_time: endTime.toISOString(),
        }),
      });

      if (response.ok) {
        fetchSleepEntries(); // Refresh the list
      } else {
        throw new Error('Failed to create sleep entry');
      }
    } catch (error) {
      console.error('Failed to create manual sleep entry:', error);
      throw error; // Re-throw to show error in modal
    }
  };

  const handleSleepEntryClick = (entry: SleepEntry) => {
    // Don't allow editing ongoing sleep entries
    if (entry.is_ongoing) return;

    setSelectedSleepEntry(entry);
    setIsEditingEntry(true);
  };

  const handleUpdateSleepEntry = async (sleepId: string, startTime: Date, endTime: Date) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/sleep/${sleepId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          start_time: startTime.toISOString(),
          end_time: endTime.toISOString(),
        }),
      });

      if (response.ok) {
        fetchSleepEntries(); // Refresh the list
      } else {
        throw new Error('Failed to update sleep entry');
      }
    } catch (error) {
      console.error('Failed to update sleep entry:', error);
      throw error;
    }
  };

  const handleDeleteSleepEntry = async (sleepId: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/sleep/${sleepId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        fetchSleepEntries(); // Refresh the list
      } else {
        throw new Error('Failed to delete sleep entry');
      }
    } catch (error) {
      console.error('Failed to delete sleep entry:', error);
      throw error;
    }
  };

  const checkOverlapWithOtherEntries = (startDateTime: Date, endDateTime: Date, currentEntryId: string): boolean => {
    // Check if the proposed time range overlaps with any other sleep entry
    return sleepEntries.some(entry => {
      // Skip the entry we're currently editing
      if (entry.id === currentEntryId) return false;

      // Skip ongoing entries (they have no end time)
      if (!entry.end_time) return false;

      // Parse entry times (backend returns with Z suffix)
      const entryStart = new Date(entry.start_time);
      const entryEnd = new Date(entry.end_time);

      // Check for overlap: (newStart < otherEnd) AND (newEnd > otherStart)
      return startDateTime < entryEnd && endDateTime > entryStart;
    });
  };

  const handleDoneEdit = () => {
    setIsEditingEntry(false);
    setSelectedSleepEntry(null);
  };

  const handleDeleteFromEdit = async () => {
    if (!selectedSleepEntry) return;

    if (window.confirm('Delete this sleep entry? This action cannot be undone.')) {
      try {
        await handleDeleteSleepEntry(selectedSleepEntry.id);
        setIsEditingEntry(false);
        setSelectedSleepEntry(null);
      } catch (err) {
        showToast('Failed to delete sleep entry', 'error');
      }
    }
  };

  const handleAdjustEditStart = async (minutes: number) => {
    if (!selectedSleepEntry || !selectedSleepEntry.end_time) return;

    // Parse current values from selected entry (backend returns with Z suffix)
    const currentStartDateTime = new Date(selectedSleepEntry.start_time);
    const newStartDateTime = new Date(currentStartDateTime.getTime() + minutes * 60 * 1000);
    const endDateTime = new Date(selectedSleepEntry.end_time);

    // Client-side validation
    const now = new Date();
    if (newStartDateTime > now) {
      showToast('Start time cannot be in the future', 'error');
      return;
    }

    if (newStartDateTime >= endDateTime) {
      showToast('Start time must be before end time', 'error');
      return;
    }

    const durationHours = (endDateTime.getTime() - newStartDateTime.getTime()) / (1000 * 60 * 60);
    if (durationHours > 24) {
      showToast('Sleep duration cannot be more than 24 hours', 'error');
      return;
    }

    // Check for overlap with other sleep entries
    if (checkOverlapWithOtherEntries(newStartDateTime, endDateTime, selectedSleepEntry.id)) {
      showToast('Sleep times cannot overlap with another sleep entry', 'error');
      return;
    }

    // Auto-save
    try {
      await handleUpdateSleepEntry(selectedSleepEntry.id, newStartDateTime, endDateTime);
      // Update selected entry with the new times immediately (don't wait for fetch)
      const durationMs = endDateTime.getTime() - newStartDateTime.getTime();
      const durationMinutes = Math.floor(durationMs / (1000 * 60));
      setSelectedSleepEntry({
        ...selectedSleepEntry,
        start_time: newStartDateTime.toISOString(),
        duration_minutes: durationMinutes
      });
    } catch (err) {
      showToast('Failed to update sleep entry', 'error');
    }
  };

  const handleAdjustEditEnd = async (minutes: number) => {
    if (!selectedSleepEntry || !selectedSleepEntry.end_time) return;

    // Parse current values from selected entry (backend returns with Z suffix)
    const startDateTime = new Date(selectedSleepEntry.start_time);
    const currentEndDateTime = new Date(selectedSleepEntry.end_time);
    const newEndDateTime = new Date(currentEndDateTime.getTime() + minutes * 60 * 1000);

    // Client-side validation
    const now = new Date();
    if (newEndDateTime > now) {
      showToast('End time cannot be in the future', 'error');
      return;
    }

    if (newEndDateTime <= startDateTime) {
      showToast('End time must be after start time', 'error');
      return;
    }

    const durationHours = (newEndDateTime.getTime() - startDateTime.getTime()) / (1000 * 60 * 60);
    if (durationHours > 24) {
      showToast('Sleep duration cannot be more than 24 hours', 'error');
      return;
    }

    // Check for overlap with other sleep entries
    if (checkOverlapWithOtherEntries(startDateTime, newEndDateTime, selectedSleepEntry.id)) {
      showToast('Sleep times cannot overlap with another sleep entry', 'error');
      return;
    }

    // Auto-save
    try {
      await handleUpdateSleepEntry(selectedSleepEntry.id, startDateTime, newEndDateTime);
      // Update selected entry with the new times immediately (don't wait for fetch)
      const durationMs = newEndDateTime.getTime() - startDateTime.getTime();
      const durationMinutes = Math.floor(durationMs / (1000 * 60));
      setSelectedSleepEntry({
        ...selectedSleepEntry,
        end_time: newEndDateTime.toISOString(),
        duration_minutes: durationMinutes
      });
    } catch (err) {
      showToast('Failed to update sleep entry', 'error');
    }
  };

  const formatTimeOnly = (dateString: string) => {
    // Backend now returns ISO strings with Z suffix for proper UTC handling
    const date = new Date(dateString);
    return date.toLocaleTimeString([], {
      hour: user.time_format_24h ? '2-digit' : 'numeric',
      minute: '2-digit',
      hour12: !user.time_format_24h
    });
  };

  const formatDateTimeForUser = (dateString: string) => {
    // Backend now returns ISO strings with Z suffix for proper UTC handling
    const date = new Date(dateString);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    const timeString = date.toLocaleTimeString([], {
      hour: user.time_format_24h ? '2-digit' : 'numeric',
      minute: '2-digit',
      hour12: !user.time_format_24h
    });

    if (dateOnly.getTime() === today.getTime()) {
      return `today at ${timeString}`;
    } else {
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);

      if (dateOnly.getTime() === yesterday.getTime()) {
        return `yesterday at ${timeString}`;
      } else {
        return date.toLocaleDateString() + ` at ${timeString}`;
      }
    }
  };

  const formatDuration = (minutes: number | null) => {
    if (minutes === null) return 'Ongoing';

    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;

    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const getEditingDuration = () => {
    if (!selectedSleepEntry) return '';
    return formatDuration(selectedSleepEntry.duration_minutes);
  };

  if (loading) {
    return (
      <div className="history-page">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="history-page">
      <div className="history-header">
        <h2>Sleep History</h2>
        <p className="baby-name">{activeBaby?.name || 'Loading...'}</p>
        <button
          className="manual-entry-btn"
          onClick={() => setShowManualEntry(true)}
        >
          + Add Sleep
        </button>
      </div>

      {isEditingEntry ? (
        /* Edit mode - show inline edit UI with buttons only */
        <div className="edit-controls">
          <div className="edit-header">
            <h3>Edit Sleep - {getEditingDuration()}</h3>
          </div>

          <div className="edit-form">
            {/* Start time adjustment */}
            <div className="edit-time-section">
              <div className="edit-time-label">
                Start: {selectedSleepEntry && formatTimeOnly(selectedSleepEntry.start_time)}
              </div>
              <div className="edit-arrow-controls">
                <button
                  className="edit-arrow-btn"
                  onClick={() => handleAdjustEditStart(-1)}
                  title="Move start time back 1 minute"
                >
                  ◄
                </button>
                <button
                  className="edit-arrow-btn"
                  onClick={() => handleAdjustEditStart(1)}
                  title="Move start time forward 1 minute"
                >
                  ►
                </button>
              </div>
            </div>

            {/* End time adjustment */}
            <div className="edit-time-section">
              <div className="edit-time-label">
                End: {selectedSleepEntry && selectedSleepEntry.end_time && formatTimeOnly(selectedSleepEntry.end_time)}
              </div>
              <div className="edit-arrow-controls">
                <button
                  className="edit-arrow-btn"
                  onClick={() => handleAdjustEditEnd(-1)}
                  title="Move end time back 1 minute"
                >
                  ◄
                </button>
                <button
                  className="edit-arrow-btn"
                  onClick={() => handleAdjustEditEnd(1)}
                  title="Move end time forward 1 minute"
                >
                  ►
                </button>
              </div>
            </div>
          </div>

          <div className="edit-actions">
            <button
              className="delete-btn-inline"
              onClick={handleDeleteFromEdit}
            >
              Delete
            </button>
            <button
              className="done-btn-inline"
              onClick={handleDoneEdit}
            >
              Done
            </button>
          </div>
        </div>
      ) : (
        /* List view mode */
        <div className="sleep-content" ref={sleepListRef}>
          {sleepEntries.length === 0 ? (
            <p className="no-entries">No sleep entries yet. Start tracking!</p>
          ) : (
            <div className="sleep-list">
              {(() => {
                const groupedEntries = groupEntriesByDate(sleepEntries);
                const sortedDates = Object.keys(groupedEntries).sort((a, b) =>
                  new Date(a).getTime() - new Date(b).getTime()
                );

                return sortedDates.map(dateKey => (
                  <div key={dateKey} className="date-group">
                    <div className="date-header">
                      <h3>{getDateHeader(dateKey)}</h3>
                    </div>
                    {groupedEntries[dateKey]
                      .sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime())
                      .map((entry) => (
                        <div
                          key={entry.id}
                          className={`sleep-entry ${entry.is_ongoing ? 'ongoing' : ''} clickable`}
                          onClick={() => handleSleepEntryClick(entry)}
                        >
                          <div className="sleep-time">
                            <div className="start-time">
                              <strong>Start:</strong> {formatDateTimeForUser(entry.start_time)}
                            </div>
                            {entry.end_time && (
                              <div className="end-time">
                                <strong>End:</strong> {formatDateTimeForUser(entry.end_time)}
                              </div>
                            )}
                          </div>
                          <div className="sleep-duration">
                            {formatDuration(entry.duration_minutes)}
                          </div>
                        </div>
                      ))
                    }
                  </div>
                ));
              })()}
            </div>
          )}
        </div>
      )}

      <ManualSleepEntry
        isOpen={showManualEntry}
        onClose={() => setShowManualEntry(false)}
        onSubmit={handleManualSleepSubmit}
        babyId={activeBaby?.id || ''}
        use24h={user.time_format_24h}
      />
    </div>
  );
};

export default History;
